import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Loader2, X, KeyRound, MapPin } from 'lucide-react';
import { authAPI, logout, persistSession } from '../lib/auth';
import {
  collectDeviceContext,
  fetchDevicePolicy,
  getLocationPermission,
  getOrCreateDeviceId,
  getPendingToken,
  getStoredDeviceName,
  requestLocation,
  setPendingToken,
} from '../lib/device-context';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const passwordRef = useRef('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [authError, setAuthError] = useState(null);
  const [returnUrl, setReturnUrl] = useState('/');
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMsg, setForgotMsg] = useState(null);
  const [devicePending, setDevicePending] = useState(false);
  const [locationPromptOpen, setLocationPromptOpen] = useState(false);
  const [locationDenied, setLocationDenied] = useState(false);
  const [locationBusy, setLocationBusy] = useState(false);
  const [totp, setTotp] = useState('');
  const [needTotp, setNeedTotp] = useState(false);
  const [pendingMsg, setPendingMsg] = useState('');
  const lastLocRef = useRef(null);
  const navigate = useNavigate();

  const finishLogin = (access_token, user) => {
    setPendingToken('');
    setDevicePending(false);
    persistSession(access_token, user);
    const dest = user?.is_poc
      ? (returnUrl.startsWith('/scan') || returnUrl.startsWith('/reports') ? returnUrl : '/scan')
      : (returnUrl.startsWith('/') ? returnUrl : '/');
    navigate(dest);
  };

  const applyLocation = async (force = false) => {
    setLocationBusy(true);
    try {
      if (!force) {
        const policy = await fetchDevicePolicy(email.trim() || undefined);
        if (policy.skip_location) {
          const loc = { ok: true, granted: true, latitude: policy.latitude, longitude: policy.longitude };
          lastLocRef.current = loc;
          setLocationPromptOpen(false);
          return collectDeviceContext(loc, getStoredDeviceName());
        }
      }
      let loc = lastLocRef.current;
      if (force || (!loc?.ok && !loc?.granted)) {
        loc = await requestLocation();
        if (loc.ok || loc.granted) lastLocRef.current = loc;
      }
      const ctx = await collectDeviceContext(loc || { ok: false, granted: false }, getStoredDeviceName());
      const ready = !!(loc?.granted || loc?.ok || ctx.location_granted);
      setLocationDenied(!!loc?.denied && !ready);
      setLocationPromptOpen(!ready);
      return ctx;
    } finally {
      setLocationBusy(false);
    }
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.history.replaceState(null, '', window.location.href);
    }
    const params = new URLSearchParams(window.location.search);
    if (params.get('reason') === 'timeout' || params.get('portal_logout') === '1') {
      logout();
    }
    const url = params.get('returnUrl');
    if (url) setReturnUrl(decodeURIComponent(url));

    const errorCode = params.get('error');
    const ssoToken = params.get('access_token');
    if (ssoToken) {
      (async () => {
        try {
          localStorage.setItem('access_token', ssoToken);
          const res = await authAPI.me();
          persistSession(ssoToken, res.data?.user);
          window.history.replaceState(null, '', window.location.pathname);
          const ssoUser = res.data?.user;
          const dest = ssoUser?.is_poc
            ? (url && (decodeURIComponent(url).startsWith('/scan') || decodeURIComponent(url).startsWith('/reports'))
              ? decodeURIComponent(url)
              : '/scan')
            : (url ? decodeURIComponent(url) : '/');
          navigate(dest);
        } catch {
          setAuthError('SSO login failed. Please try again or contact support.');
          logout();
        }
      })();
      return;
    }
    if (errorCode === 'no_user') {
      setAuthError('User not provisioned in MCC platform. Contact your administrator.');
    } else if (errorCode === 'login_required') {
      setAuthError('Your Portal session expired. Sign in at portal.paylegit.in, then click Open again.');
    } else if (errorCode === 'poc_local_only') {
      setAuthError(
        'POC accounts use email + temporary password from signup (not Portal SSO). Sign in with the credentials emailed to you, or use Forgot password.',
      );
    } else if (errorCode === 'auth_error') {
      const detail = params.get('detail');
      setAuthError(
        detail
          ? `SSO login failed (${detail}). Please try again or contact support.`
          : 'SSO login failed. Please try again or contact support.',
      );
    } else if (errorCode === 'session_save') {
      setAuthError('Login session could not be created. Please try again.');
    } else {
      setAuthError(null);
    }
    if (params.get('device_pending') === '1' || getPendingToken()) {
      setDevicePending(true);
    }
    void (async () => {
      const policy = await fetchDevicePolicy();
      if (policy.skip_location) {
        lastLocRef.current = { ok: true, granted: true, latitude: policy.latitude, longitude: policy.longitude };
        setLocationPromptOpen(false);
        await collectDeviceContext(lastLocRef.current, getStoredDeviceName());
        return;
      }
      const perm = await getLocationPermission();
      if (perm === 'granted') {
        const loc = await requestLocation();
        if (loc.ok || loc.granted) lastLocRef.current = loc;
        await collectDeviceContext(loc, getStoredDeviceName());
        setLocationDenied(false);
        setLocationPromptOpen(!(loc.granted || loc.ok));
        return;
      }
      setLocationPromptOpen(true);
      setLocationDenied(perm === 'denied');
      await collectDeviceContext({ ok: false, granted: false }, getStoredDeviceName());
    })();
  }, []);

  const handlePaylegitSSO = async () => {
    const ctx = await applyLocation();
    if (!ctx?.location_granted) {
      setLocationPromptOpen(true);
      return;
    }
    window.location.href = `/api/auth/paylegit?returnUrl=${encodeURIComponent(returnUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const pwd = password || passwordRef.current;
    passwordRef.current = pwd;
    setPassword('');
    try {
      let ctx;
      try {
        ctx = await applyLocation();
      } catch {
        ctx = await collectDeviceContext({ ok: false, granted: false }, getStoredDeviceName());
      }
      if (!ctx?.device_id) {
        ctx = { ...ctx, device_id: getOrCreateDeviceId() };
      }
      if (!ctx.location_granted) {
        setLocationPromptOpen(true);
        setError('Location access is mandatory, including on first login.');
        return;
      }
      const res = await authAPI.login(email, pwd, {
        totp_code: totp || undefined,
        device_id: ctx.device_id,
        machine_name: ctx.machine_name,
        device_name: ctx.device_name,
        ipv4: ctx.ipv4,
        ipv6: ctx.ipv6,
        latitude: ctx.latitude,
        longitude: ctx.longitude,
        location_granted: ctx.location_granted,
      });
      const { access_token, user } = res.data;
      passwordRef.current = '';
      finishLogin(access_token, user);
    } catch (err) {
      const data = err.response?.data || {};
      if (data.device_pending && data.pending_token) {
        setPendingToken(data.pending_token);
        setDevicePending(true);
        setPendingMsg(data.detail || '');
        setError(null);
        return;
      }
      if (data.totp_required) {
        setNeedTotp(true);
        setError(data.detail || 'Authenticator code required.');
        return;
      }
      if (data.location_required) {
        setLocationPromptOpen(true);
        setError(data.detail || 'Location access is required.');
        return;
      }
      setError(data.detail || 'Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const checkApproval = async () => {
    const pending = getPendingToken();
    if (!pending) {
      setPendingMsg('Verification session expired. Sign in again.');
      return;
    }
    try {
      const res = await authAPI.deviceStatus(pending);
      if (res.data?.allowed && res.data.access_token) {
        finishLogin(res.data.access_token, res.data.user);
        return;
      }
      setPendingMsg(res.data?.detail || 'Still waiting for administrator approval.');
    } catch (err) {
      setPendingMsg(err.response?.data?.detail || 'Could not check device status.');
    }
  };

  const handleForgotPassword = async () => {
    if (!forgotEmail) return;
    setForgotLoading(true);
    setForgotMsg(null);
    try {
      const email = forgotEmail.trim().toLowerCase();
      // POC endpoint only succeeds with a real reset for is_poc accounts;
      // for everyone else it returns a generic ok without sending mail.
      const res = await fetch('/api/poc/password-reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.email_sent) {
        setForgotMsg(
          data.message ||
            'A temporary password was sent to your company email. Check your inbox.',
        );
        if (data.temp_password) {
          setForgotMsg(
            (data.message || 'Password reset.') +
              ` Temporary password: ${data.temp_password}`,
          );
        }
        return;
      }
      // Non-POC or email not sent — try standard forgot (generic ack).
      await authAPI.forgotPassword(email);
      setForgotMsg(
        data.message ||
          'If this account exists, a password reset email has been sent (POC users receive a temporary password).',
      );
    } catch (err) {
      setForgotMsg(err.response?.data?.detail || 'Failed to process request.');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-gradient-to-br from-[#0f2240] via-[#1B365D] to-[#1B365D]">

      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12 relative overflow-hidden">
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-[#1B365D]/60 blur-3xl" />
        <div className="absolute bottom-0 right-0 w-80 h-80 rounded-full bg-[#F5A623]/20 blur-3xl" />

        <div className="relative z-10">
          <img
            src="/logo/PayLegit.png"
            alt="PayLegit"
            className="h-12 w-auto"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        </div>

        <div className="relative z-10">
          <h2 className="text-4xl font-extrabold text-white leading-tight mb-4">
            MCC Risk<br />
            <span className="text-[#F5A623]">Analysis</span><br />
            &amp; Classification
          </h2>
          <p className="text-blue-200 text-base leading-relaxed max-w-xs">
            Classify merchants with AI-powered MCC detection, risk scoring, content
            analysis, and real-time monitoring — all in one platform.
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            {['MCC Classification', 'Risk Scoring', 'Content Analysis', 'Bulk Upload'].map(tag => (
              <span key={tag} className="bg-white/10 text-blue-100 text-xs px-3 py-1.5 rounded-full border border-white/20">
                {tag}
              </span>
            ))}
          </div>
        </div>

        <p className="relative z-10 text-blue-400 text-xs">
          &copy; {new Date().getFullYear()} PayLegit. All rights reserved.
        </p>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 bg-white lg:rounded-l-3xl lg:shadow-2xl">
        <div className="w-full max-w-sm">

          <div className="flex lg:hidden items-center gap-2 mb-8">
            <img
              src="/logo/PayLegit.png"
              alt="PayLegit"
              className="h-8 w-auto"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          </div>

          <h1 className="text-2xl font-bold text-slate-900 mb-1">Welcome back</h1>
          <p className="text-slate-500 text-sm mb-8">Sign in to MCC Risk Analysis Platform</p>

          {devicePending ? (
            <div className="mb-6 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3">
              <p className="text-sm font-semibold text-amber-900">New device verification required</p>
              <p className="text-sm text-amber-800 mt-1">
                This device isn&apos;t registered for your admin account. Your registration request has been sent for approval.
              </p>
              <p className="text-sm text-amber-800 mt-1">
                Please wait for an administrator to approve it in Live Server Monitor before continuing.
              </p>
              {pendingMsg ? <p className="text-sm text-amber-900 mt-2">{pendingMsg}</p> : null}
              <button
                type="button"
                className="mt-3 text-sm font-semibold text-[#1B365D] underline"
                onClick={checkApproval}
              >
                Check approval status
              </button>
            </div>
          ) : null}

          {locationPromptOpen && !devicePending ? (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0f2240]/70 p-4">
              <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl border border-slate-200" role="dialog" aria-modal="true">
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#F5A623]/15 text-[#F5A623]">
                  <MapPin className="h-7 w-7" />
                </div>
                <h2 className="text-xl font-bold text-slate-900 text-center">Allow location access</h2>
                <p className="text-sm text-slate-500 text-center mt-2">
                  MCC Online needs this computer&apos;s location to register the device. Location access is mandatory, including on first login. This prompt stays open for 60 seconds while GPS is requested.
                </p>
                {locationDenied ? (
                  <div className="mt-4 rounded-lg bg-amber-50 text-amber-900 text-sm px-3 py-2 border border-amber-200">
                    Location is blocked for this site. Click the lock or tune icon in the address bar, set Location to Allow, then tap the button again.
                  </div>
                ) : null}
                <button
                  type="button"
                  disabled={locationBusy}
                  onClick={() => { void applyLocation(true); }}
                  className="mt-5 w-full py-3 bg-gradient-to-r from-[#F5A623] to-[#E8941E] text-white text-sm font-bold rounded-xl disabled:opacity-50"
                >
                  {locationBusy ? 'Waiting for GPS (up to 60 seconds)…' : 'Allow location access'}
                </button>
              </div>
            </div>
          ) : null}

          {authError && (
            <div className="mb-6 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3">
              <p className="text-sm font-semibold text-amber-900">Access Required</p>
              <p className="text-sm text-amber-800 mt-1">{authError}</p>
              <p className="text-sm text-amber-900 mt-2">
                Need help? Contact{' '}
                <a href="mailto:contact@paylegit.in" className="font-semibold underline hover:text-amber-700">
                  contact@paylegit.in
                </a>
              </p>
            </div>
          )}

          {error && (
            <div className="mb-6 rounded-xl border border-red-300 bg-red-50 px-4 py-3">
              <p className="text-sm font-semibold text-red-900">Login Failed</p>
              <p className="text-sm text-red-800 mt-0.5">{error}</p>
            </div>
          )}

          <div className="space-y-3 mb-6">
            <button
              type="button"
              onClick={() => { void handlePaylegitSSO(); }}
              className="w-full flex items-center justify-center gap-3 px-4 py-2.5 border border-slate-200 rounded-xl text-sm font-medium text-slate-700 hover:bg-blue-50 hover:border-blue-300 transition-all shadow-sm"
            >
              <KeyRound className="h-4 w-4 text-blue-600" />
              Continue with PayLegit SSO
            </button>
          </div>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-100" />
            </div>
            <div className="relative flex justify-center text-xs text-slate-400">
              <span className="bg-white px-3">or continue with email</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                Email or Username
              </label>
              <input
                type="text"
                required
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="none"
                spellCheck={false}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                disabled={loading}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1B365D] focus:border-transparent transition-shadow disabled:opacity-50"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Password
                </label>
                <button
                  type="button"
                  onClick={() => { setForgotOpen(true); setForgotMsg(null); setForgotEmail(''); }}
                  className="text-xs text-slate-400 hover:text-[#1B365D] hover:underline"
                >
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <input
                  type={showPwd ? 'text' : 'password'}
                  required={!needTotp}
                  autoComplete="new-password"
                  autoCorrect="off"
                  spellCheck={false}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  disabled={loading}
                  className="w-full px-4 py-2.5 pr-11 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1B365D] focus:border-transparent transition-shadow disabled:opacity-50"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(!showPwd)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  tabIndex={-1}
                >
                  {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {needTotp ? (
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                  Authenticator code
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={totp}
                  onChange={(e) => setTotp(e.target.value)}
                  placeholder="123456"
                  disabled={loading}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1B365D] focus:border-transparent disabled:opacity-50"
                />
              </div>
            ) : null}

            <button
              type="submit"
              disabled={loading || locationBusy}
              className="w-full py-3 bg-gradient-to-r from-[#F5A623] to-[#E8941E] hover:from-[#E8941E] hover:to-[#d4840f] text-white text-sm font-bold rounded-xl shadow-lg transition-all disabled:opacity-50 mt-2"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Signing in…
                </span>
              ) : (
                'Sign In to Platform →'
              )}
            </button>
          </form>

          <p className="text-center text-xs text-slate-400 mt-6">
            Protected by enterprise-grade security.<br />
            Your data is encrypted in transit and at rest.
          </p>
          <p className="text-center text-sm text-slate-600 mt-4">
            New to MCC as a Service?{' '}
            <Link to="/poc/signup" className="text-blue-700 font-medium hover:underline">
              Sign up for a free Online MCC POC (up to 10 merchants)
            </Link>
          </p>
        </div>
      </div>

      {forgotOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-900">Reset your password</h3>
              <button
                onClick={() => setForgotOpen(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm text-slate-500 mb-4">
              Enter your email. POC accounts receive a temporary password; other accounts get a reset link if they exist.
            </p>
            <input
              type="email"
              value={forgotEmail}
              onChange={(e) => setForgotEmail(e.target.value)}
              placeholder="you@company.com"
              className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1B365D] focus:border-transparent mb-3"
            />
            {forgotMsg && (
              <p className="text-sm text-emerald-700 mb-3">{forgotMsg}</p>
            )}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setForgotOpen(false)}
                className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handleForgotPassword}
                disabled={forgotLoading || !forgotEmail}
                className="flex-1 py-2.5 rounded-xl bg-[#1B365D] text-white text-sm font-semibold hover:bg-[#0f2240] transition-colors disabled:opacity-50"
              >
                {forgotLoading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
